import { inlinePurchasePayloadSchema, remotePurchasePayloadSchema } from '../../api/impl/createTelegramAPI';
import { trackSubscriptionUpdate } from '../../api/impl/PlatformChatbotAPI';
import type { PurchaseInfo } from '../../api/ReplicantAPI';
import {
    AnalyticsEvent,
    CHATBOT_WEBHOOK,
    PostAnalyticsFN,
    TELEGRAM_PAYMENT_SUCCESS,
    TELEGRAM_PRE_CHECKOUT_QUERY,
} from '../../common/Analytics';
import type { ErrorReporter } from '../../common/ErrorReporter';
import type DB from '../../db/DB';
import { ReplicantError } from '../../Errors';
import type { Replicant } from '../../ReplicantConfig';
import SB from '../../SchemaBuilder';
import { duration } from '../../utils/Utils';
import type { APIAccessInterface, PushDocsToIndexFn } from '../ServerReplicant';
import type { TelegramClient } from '../Telegram';
import { createRevenueValidateEvent, TELEGRAM_STARS_CURRENCY_CODE } from '../utils/revenueUtils';
import { executeOnWebhook } from './executeOnWebhook';

export type TelegramWebhookHandler = {
    handleEvent(eventRequest: { body: unknown; secretToken: string }): Promise<void>;
};

const chatSchema = SB.object({
    id: SB.int(),
}).additionalProperties();

const userSchema = SB.object({
    id: SB.int(),
    is_bot: SB.boolean(),
    first_name: SB.string(),
    last_name: SB.string().optional(),
    language_code: SB.string().optional(),
    username: SB.string().optional(),
}).additionalProperties();

const successfulPaymentSchema = SB.object({
    currency: SB.string(),
    invoice_payload: SB.string(),
    telegram_payment_charge_id: SB.string(),
    total_amount: SB.int(),
}).additionalProperties();

const messageSchema = SB.object({
    chat: chatSchema,
    message_id: SB.int(),
    from: userSchema.optional(),
    date: SB.int(),
    text: SB.string().optional(),
    successful_payment: successfulPaymentSchema.optional(),
}).additionalProperties();

const myChatMemberSchema = SB.object({
    chat: SB.object({ type: SB.string() }).additionalProperties(),
    date: SB.int(),
    from: SB.object({ id: SB.int(), is_bot: SB.boolean() }).additionalProperties(),
    new_chat_member: SB.object({ status: SB.string() }).additionalProperties(),
}).additionalProperties();

const preCheckoutQuerySchema = SB.object({
    id: SB.string(),
    from: userSchema,
    currency: SB.string(),
    total_amount: SB.int(),
    invoice_payload: SB.string(),
}).additionalProperties();

const eventBodySchema = SB.object({
    update_id: SB.int(),
    message: messageSchema.optional(),
    my_chat_member: myChatMemberSchema.optional(),
    pre_checkout_query: preCheckoutQuerySchema.optional(),
}).additionalProperties();

/** @see https://core.telegram.org/bots/api#message */
export type TelegramWebhookEvent = SB.ExtractType<typeof eventBodySchema> & { platform: 'telegram' };

export function createTelegramWebhookHandler<T extends Replicant>(opts: {
    apiAccess: APIAccessInterface<T>;
    db: DB<T>;
    errorReporter: ErrorReporter;
    pushDocsToIndex: PushDocsToIndexFn;
    sendAnalyticsEvents: PostAnalyticsFN;
    telegramClient: TelegramClient;
}): TelegramWebhookHandler {
    async function resolvePurchasePayload(
        rawPayload: string,
    ): Promise<{ productId: string; analytics?: { [key: string]: unknown; feature: string; subFeature: string } }> {
        const payload = JSON.parse(rawPayload);

        if (!inlinePurchasePayloadSchema.isValid(payload)) {
            throw Error(`Invalid Telegram purchase payload: ${rawPayload}`);
        }

        // Try looking up payload from KV store:
        if (payload.key) {
            try {
                const kvStorePayload = await opts.apiAccess.kvStore.get(payload.key);

                if (kvStorePayload) {
                    const parsedKvStorePayload = JSON.parse(kvStorePayload);

                    if (remotePurchasePayloadSchema.isValid(parsedKvStorePayload)) {
                        return { ...parsedKvStorePayload, productId: payload.productId };
                    }
                }
            } catch {}
        }

        // Fall back to inline payload:
        return { productId: payload.productId };
    }

    /** Handle IAP pre-checkout query https://core.telegram.org/bots/api#precheckoutquery. */
    async function handlePreCheckoutQuery(
        preCheckoutQuery: SB.ExtractType<typeof preCheckoutQuerySchema>,
    ): Promise<void> {
        const userId = preCheckoutQuery.from.id.toString();

        const { analytics, productId } = await resolvePurchasePayload(preCheckoutQuery.invoice_payload);

        await opts.telegramClient.answerPreCheckoutQuery({
            preCheckoutQueryId: preCheckoutQuery.id,
        });

        await opts.sendAnalyticsEvents([
            {
                user_id: userId,
                event_type: TELEGRAM_PRE_CHECKOUT_QUERY,
                event_properties: {
                    ...analytics,
                    currency: preCheckoutQuery.currency,
                    price: preCheckoutQuery.total_amount,
                    productId,
                },
            },
        ]);
    }

    async function handleSuccessfulPayment(
        userId: number,
        successfulPayment: SB.ExtractType<typeof successfulPaymentSchema>,
        purchasedAt: number,
    ): Promise<void> {
        if (successfulPayment.currency !== 'XTR') {
            throw Error(`Unexpected currency in Telegram payment event: ${JSON.stringify(successfulPayment)}`);
        }

        const autoRefundIsEnabled = process.env.STAGE !== 'prod' && process.env.TELEGRAM_ENVIRONMENT !== 'test';

        if (autoRefundIsEnabled) {
            await opts.telegramClient.refundStarPayment({
                userId,
                telegramPaymentChargeId: successfulPayment.telegram_payment_charge_id,
            });
        }

        const { analytics, productId } = await resolvePurchasePayload(successfulPayment.invoice_payload);

        const purchase: PurchaseInfo = {
            algorithm: 'telegramStars',
            developer_payload: '',
            is_consumed: false,
            issued_at: 0,
            payment_id: successfulPayment.telegram_payment_charge_id,
            product_id: productId,
            purchase_time: purchasedAt,
            purchase_token: '',
        };

        await opts.db.chatbot.awardPurchase(userId.toString(), purchase);

        const priceInStars = successfulPayment.total_amount;

        const paymentSuccessEvent: AnalyticsEvent<'TelegramPaymentSuccess'> = {
            user_id: userId.toString(),
            event_type: TELEGRAM_PAYMENT_SUCCESS,
            event_properties: {
                ...analytics,
                currency: successfulPayment.currency,
                price: priceInStars,
                productId,
            },
        };

        const analyticsEvents: AnalyticsEvent[] = [paymentSuccessEvent];

        const revenueValidateEvent = await createRevenueValidateEvent({
            convertCurrency: opts.apiAccess.exchangeRateHandler.convert,
            currency: TELEGRAM_STARS_CURRENCY_CODE,
            extraEventProperties: analytics,
            price: priceInStars,
            productId,
            provider: 'telegram',
            providerMethod: 'telegram_stars',
            userId: userId.toString(),
        });

        if (revenueValidateEvent) {
            analyticsEvents.push(revenueValidateEvent);
        }

        await opts.sendAnalyticsEvents(analyticsEvents);
    }

    return {
        async handleEvent({ body, secretToken }) {
            if (!process.env.TELEGRAM_WEBHOOK_SECRET_TOKEN) {
                throw Error('TELEGRAM_WEBHOOK_SECRET_TOKEN must be configured before receiving Telegram webhooks');
            }

            if (secretToken !== process.env.TELEGRAM_WEBHOOK_SECRET_TOKEN) {
                throw Error('Invalid Telegram webhook secret token');
            }

            if (!eventBodySchema.isValid(body)) {
                throw Error('Unexpected Telegram webhook request body: ' + JSON.stringify(body));
            }

            if (body.message?.successful_payment && body.message.from) {
                const purchasedAt = duration({ seconds: body.message.date });

                await handleSuccessfulPayment(body.message.from.id, body.message.successful_payment, purchasedAt);
            }

            if (body.message) {
                const messageSenderId = body.message.from?.id?.toString();
                if (!messageSenderId) {
                    return;
                }

                const webhookEvent = { ...body, platform: 'telegram' as const };

                const analyticsToSend = [createChatbotWebhookAnalyticEvent(messageSenderId, webhookEvent)];

                // update the chatbot subscription status before passing the event to the webhook handler
                const chatbotMetainfo = await opts.db.chatbot.fetchMetainfo(messageSenderId);

                if (chatbotMetainfo?.subscribed !== true) {
                    analyticsToSend.push(
                        trackSubscriptionUpdate(messageSenderId.toString(), true, {
                            timestamp: body.message!.date * 1000,
                        }),
                    );
                    await opts.db.chatbot.markSubscribed(messageSenderId, true, body.message!.date * 1000);
                }

                // start sending the analytics events before executing the webhook handler
                const sendingAnalyticsPromise = opts.sendAnalyticsEvents(analyticsToSend);

                try {
                    const { newIndexDoc } = await executeOnWebhook(
                        messageSenderId,
                        [webhookEvent],
                        opts.apiAccess,
                        opts.db,
                    );

                    if (newIndexDoc) {
                        await opts.pushDocsToIndex({ [messageSenderId]: newIndexDoc });
                    }
                } catch (error: any) {
                    const replicantError = new ReplicantError(
                        'Telegram webhook event handler failed: ' + error.message,
                        'server_error',
                    );
                    replicantError.extras = { event: body, reason: error };
                    replicantError.stack = error.stack;

                    await opts.errorReporter.captureException(replicantError);
                }

                // wait for the analytics events to be sent before returning
                await sendingAnalyticsPromise;
            }

            if (body.pre_checkout_query) {
                await handlePreCheckoutQuery(body.pre_checkout_query);
            }

            // Track bot subscription analytics on bot chat block and unblock events:
            if (body.my_chat_member) {
                const { chat, date, from, new_chat_member } = body.my_chat_member;

                const botSubscriptionChanged =
                    chat.type === 'private' && !from.is_bot && ['member', 'kicked'].includes(new_chat_member.status);

                if (botSubscriptionChanged) {
                    const isSubscribed = new_chat_member.status === 'member';

                    const subscriptionEvent = trackSubscriptionUpdate(from.id.toString(), isSubscribed, {
                        timestamp: date * 1000,
                    });

                    await Promise.all([
                        opts.db.chatbot.markSubscribed(from.id.toString(), isSubscribed, date * 1000),
                        opts.sendAnalyticsEvents([subscriptionEvent]),
                    ]);
                }
            }
        },
    };
}

function createChatbotWebhookAnalyticEvent(userId: string, event: TelegramWebhookEvent): AnalyticsEvent {
    return {
        user_id: userId,
        event_type: CHATBOT_WEBHOOK,
        event_properties: event as any,
    };
}
